import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';
import Topbar from '../components/Topbar';
import { fetchAdminSummary } from '../../api/auth';
import { getLocalItem, getSessionItem } from '../../utils/safeStorage';
import { io } from 'socket.io-client';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { canAccessAdminPath, resolveAdminLandingPath } from '../utils/adminNavigation';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const { user, accessToken, hasPermission, isSuperAdmin } = useAdminAuth();
  const [summary, setSummary] = useState({
    totalUsers: 0,
    totalBookings: 0,
    pendingPayments: 0,
    openTickets: 0,
    unreadMessages: 0,
    unreadPackageEnquiries: 0,
  });
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = window.localStorage.getItem('admin.sidebar.collapsed');
    if (stored === null) return false;
    return stored === '1' || stored === 'true';
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!accessToken || (!isSuperAdmin && !hasPermission('DASHBOARD_VIEW'))) {
      setSummary({
        totalUsers: 0,
        totalBookings: 0,
        pendingPayments: 0,
        openTickets: 0,
        unreadMessages: 0,
        unreadPackageEnquiries: 0,
      });
      return undefined;
    }
    const token = accessToken || getSessionItem('adminAccessToken') || getLocalItem('adminAccessToken') || '';
    if (!token) return;
    let active = true;
    const load = async () => {
      setError('');
      try {
        const data = await fetchAdminSummary(token);
        if (active) setSummary(data);
      } catch (err) {
        if (active) setError(err.message || 'Unable to load summary.');
      }
    };
    load();
    const apiBase = process.env.REACT_APP_API_BASE_URL || '';
    const socket = io(apiBase, { transports: ['websocket'], withCredentials: true });
    socket.on('enquiry_created', load);
    return () => {
      active = false;
      socket.off('enquiry_created', load);
      socket.disconnect();
    };
  }, [accessToken, hasPermission, isSuperAdmin]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('admin.sidebar.collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem('admin.sidebar.collapsed');
    if (stored !== null) return;
    const onResize = () => {
      if (window.innerWidth < 1024) {
        setCollapsed(true);
      }
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (user?.mustChangePassword && location.pathname !== '/admin/change-password') {
    return <Navigate to="/admin/change-password" replace />;
  }

  if (location.pathname === '/admin' && !isSuperAdmin && !hasPermission('DASHBOARD_VIEW')) {
    const target = resolveAdminLandingPath({ user, hasPermission, isSuperAdmin });
    if (target && target !== '/admin') {
      return <Navigate to={target} replace />;
    }
  }

  if (
    location.pathname.startsWith('/admin') &&
    location.pathname !== '/admin/change-password' &&
    !canAccessAdminPath({ pathname: location.pathname, hasPermission, isSuperAdmin })
  ) {
    const target = resolveAdminLandingPath({ user, hasPermission, isSuperAdmin });
    if (target && target !== location.pathname) {
      return <Navigate to={target} replace />;
    }
  }

  return (
    <div className="admin-shell min-h-screen bg-[var(--admin-bg)] text-[color:var(--admin-text)]">
      <AdminSidebar
        summary={summary}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((v) => !v)}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />
      <div
        className={`flex min-h-screen flex-col transition-[margin] duration-300 ${
          collapsed ? 'lg:ml-[var(--admin-sidebar-collapsed-width)]' : 'lg:ml-[var(--admin-sidebar-width)]'
        }`}
      >
        <Topbar
          onToggleSidebar={() => setCollapsed((v) => !v)}
          isCollapsed={collapsed}
          onOpenMobile={() => setMobileOpen(true)}
        />
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {error && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
              {error}
            </div>
          )}
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;


